"use client"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Textarea } from "@/components//ui/textarea"
import { MessageCircle, Send, X } from "lucide-react"
import { Separator } from "../ui/separator"
import { useEffect, useState } from "react"
import { submitFeedback } from "@/lib/submit_feedback";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { cn } from "@/lib/utils";

type ButtonProps = React.ComponentProps<typeof Button>;

interface FeedbackDialogProps {
  /** Styling for the trigger button, so a caller can match its surroundings —
   *  the paragraph action pill uses a smaller, borderless variant. Defaults
   *  preserve the standalone look used everywhere else. */
  triggerVariant?: ButtonProps["variant"];
  triggerSize?: ButtonProps["size"];
  /** Side the trigger's tooltip opens on. */
  tooltipSide?: "top" | "bottom" | "left" | "right";
}

function FeedbackDialog({
  triggerVariant = "secondary",
  triggerSize = "icon",
  tooltipSide = "bottom",
}: FeedbackDialogProps = {}) {

  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);

  const handleSubmit = async () => {
    if (!text.trim()) {
      toast.error("Please enter some feedback first!");
      return;
    }

    try {
      // --- Your Supabase/API logic here ---
      submitFeedback({
        targetType: 'app_ux',
        text: text
      });

      // 3. Clear the input for next time
      setText("");
      setOpen(false);
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen} >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
                variant={triggerVariant}
                size={triggerSize}
                onClick={() => setOpen(true)}
              >
                <MessageCircle className="h-4 w-4" />
              </Button>
          </TooltipTrigger>
          <TooltipContent side={tooltipSide}>
            Provide feedback
          </TooltipContent>
        </Tooltip>
      <DialogContent
        className=""
      >
        <DialogTitle>
          Feedback
        </DialogTitle>
        <div className="flex flex-col justify-start items-start gap-4">
          <div className="flex flex-col justify-start items-start gap-1">
            <div className="text-muted-foreground text-sm">
              Have an idea to improve LawLibrary.AI? We'd love to hear from you.
            </div>
          </div>
          <Textarea
            id="feedback"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="My idea is..."
            className="min-h-32 resize-none"
          />
          <div className="w-full flex flex-row justify-end items-center gap-2">
            <Button
              variant="secondary"
              size="icon"
              onClick={() => setOpen(false)}
              className="rounded-full"
            >
              <X />
            </Button>
            <Button
              variant="default"
              size="icon"
              onClick={handleSubmit}
              className="rounded-full"
            >
              <Send />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export { FeedbackDialog }
